
import { ImageWidget } from "./components/CustomWidgets"
import React from "react"
import { DateTime } from "luxon"
import { SampleImageRenderer } from "./SampleImageRenderer"
import { DataProvider, ListComponent } from "./GenericList"

// Matches the exact format the live app reads offers with
// (win-facil-web-app's UserContext.tsx `getThisMonthOffers`) — offers are scoped under a
// per-month subcollection, so this editor only ever manages the *current* month's offers.
// Historical month management is out of scope for this pass. See Trello #211.
export const getCurrentOffersMonthKey = () => DateTime.now().toFormat("LLyyyy")

const getSortableNumber = (value: unknown, fallback: number) => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : fallback
}

const sortOffersByDateThenOrder = (first: any, second: any) => {
  const firstDate = getSortableNumber(first?.expirationDate ?? first?.createdDate, 0)
  const secondDate = getSortableNumber(second?.expirationDate ?? second?.createdDate, 0)

  if (firstDate !== secondDate) {
    return secondDate - firstDate
  }

  return getSortableNumber(first?.order, Number.MAX_SAFE_INTEGER) -
    getSortableNumber(second?.order, Number.MAX_SAFE_INTEGER)
}

export const businessOfferComponentData = {
  getListLocation(id: any) {
    // Was pointed at `businesses/{id}/offers`, which the live app never reads from — the
    // app reads/writes `sponsors/{id}/offers/{month}/offers` (see
    // win-facil-web-app/src/components/stores/UserContext.tsx's `getThisMonthOffers`). This
    // editor was consequently unrouted/unreachable (not in routes.tsx) since it couldn't
    // have managed real data. Fixed to the real path and wired into SponsorTabs. See Trello
    // #211.
    return ["sponsors", id, "offers", getCurrentOffersMonthKey(), "offers"]
  },

  EDIT_TITLE: "Edit Offer",
  ADD_TITLE: "Add Offer",
  LOADING_MESSAGE: "Loading offers...",
  TITLE: "Sponsor Offers (current month)",
  IMAGES_BUCKET: "offersImages",
  prepareListData: (items: any[]) => [...items].sort(sortOffersByDateThenOrder),

  columnsProperties: [
    { hidden: true, name: "id", label: "Id" },
    { required: false, name: "disabled", default: false, label: "Disabled", type: "boolean" },

    { required: true, name: "expirationDate", label: "Expiration Date" },
    { required: true, name: "name", label: "Name" },
    { required: true, name: "order", label: "Order" },
    {
      required: false,
      name: "showWinBox",
      default: true,
      label: "Show Win Box",
      type: "boolean",
    },
    { required: false, type: "string", name: "winBoxLabel", label: "Win Box Label" },
    {
      hidden: false,
      type: "string",
      required: false,
      name: "winBoxImageUrl",
      label: "Win Box Image",
      options: {
        customBodyRender: SampleImageRenderer(),
      },
    },
    {
      required: false,
      name: "winCouponPerLike",
      default: false,
      label: "Grant Coupon Per Like",
      type: "boolean",
    },
    {
      required: false,
      type: "number",
      name: "likesCount",
      label: "Likes",
      hideEdit: true,
    },
    {
      hidden: false,
      type: "string",
      required: true,
      name: "icon",
      label: "Icon",
      options: {
        customBodyRender: SampleImageRenderer(),
      },
    },
    {
      hidden: false,
      type: "array",
      required: false,
      name: "images",
      label: "Images",
      options: {
        customBodyRender: SampleImageRenderer(),
      },
      items: {
        type: "object",
        properties: {
          title: {
            type: "string",
            title: "Title",
          },
          link: {
            type: "string",
            title: "Link",
          },
          image: {
            type: "string",
            title: "Image",
          },
          // Mirrors the raffle slide config (react-ui-components `IRaffle.imageResizeMode`)
          // so admins configure sponsor offer media the same way they configure raffle
          // slides. See Trello #220.
          type: {
            type: "string",
            title: "Media Type",
            enum: ["image", "video"],
            default: "image",
          },
          videoUrl: {
            type: "string",
            title: "Video URL (used when Media Type is Video — YouTube, Vimeo, Facebook, Instagram, TikTok, or a direct .mp4 link)",
          },
          objectFit: {
            type: "string",
            title: "Aspect Mode",
            enum: ["contain", "cover", "fill", "none", "scale-down"],
            default: "contain",
          },
        },
      },
    },
    { required: true, name: "description", label: "Description" },
    { required: true, name: "language", label: "Language" },
    { required: true, name: "country", label: "Country" },
    { required: false, name: "state", label: "State" },
    { required: false, name: "city", label: "City" },
    { required: false, name: "street", label: "Street" },
    { required: false, name: "county", label: "County" },
    { required: false, name: "zipCode", label: "Zip Code" },
  ],

  uiSchema: {
    icon: {
      "ui:widget": ImageWidget,
    },
    images: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    winBoxImageUrl: {
      "ui:widget": ImageWidget,
    },
    likesCount: {
      "ui:disabled": true,
    },
    expirationDate: {
      "ui:widget": "datetime",
    },

    description: {
      "ui:widget": "textarea",
    },
  },

  async imageProcessor(fromData: { images: any[]; icon?: string; winBoxImageUrl?: string }, handleUpload: (arg0: any, arg1: string, arg2: any, arg3: { [x: string]: any; }) => any, id: any) {
    if (fromData.images?.length > 0) {
      const imagesURLs = await Promise.all(
        fromData.images.map(({ image, ...rest }) => {
          return handleUpload(id, this.IMAGES_BUCKET, image, rest)
        }),
      )
      fromData.images = imagesURLs
    }

    if (fromData.icon) {
      fromData.icon = await handleUpload(id, this.IMAGES_BUCKET, fromData.icon, undefined)
    }

    if (fromData.winBoxImageUrl) {
      fromData.winBoxImageUrl = await handleUpload(id, this.IMAGES_BUCKET, fromData.winBoxImageUrl, undefined)
    }

    return fromData
  },
}

export const BusinessOffersComponent = ({ businessId }: any) => {
  return (
    <DataProvider {...businessOfferComponentData} mainId={businessId}>
      <ListComponent />
    </DataProvider>
  )
}
